import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react-router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";
import { formatCurrency, formatDate } from "@/lib/formatters";
import { getAuthHeaders } from "@/lib/api";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags, setMetaRobots } from "@/lib/seo";
import { generateWebPageSchema, generateBreadcrumbListSchema, addSchemaToPage } from "@/lib/schema";

type DashboardData = {
  totalIncome: number;
  totalExpenses: number;
  totalFutureExpenses: number;
  monthlyBreakdown: number;
  monthlyExpenses: number;
  regularBillsTotal: number;
  provisionsTotal: number;
  totalMonthlySavings: number;
  remainingBalance: number;
  regularExpenses: any[]; // Using any[] for simplicity, could define Expense type shared
  futureExpenses: any[];
  savings: any[];
};

export default function Dashboard() {
  useEffect(() => {
    const title = "Dashboard - Munthly";
    const description = "Track your income, expenses, and savings with Munthly's free budgeting dashboard. Monitor your monthly balance sheet and stay in control of your finances.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/dashboard");
    const ogCleanup = setOpenGraphTags(title, description, "/dashboard");
    const twitterCleanup = setTwitterCardTags(title, description);
    const robotsCleanup = setMetaRobots("noindex, nofollow"); // Protected page - don't index
    
    // Add schema for dashboard page
    const webPageSchema = generateWebPageSchema(
      "/dashboard",
      title,
      description
    );
    const breadcrumbSchema = generateBreadcrumbListSchema([
      { name: "Home", url: "/" },
      { name: "Dashboard", url: "/dashboard" }
    ]);
    
    const schemaCleanup = addSchemaToPage([webPageSchema, breadcrumbSchema]);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      robotsCleanup();
      schemaCleanup();
    };
  }, []);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [filter, setFilter] = useState<"all" | "regular" | "provision" | "savings">("all");

  useEffect(() => {
    // Only fetch if Clerk is loaded and user is signed in
    if (!isLoaded) {
      // Still loading, wait
      return;
    }
    
    if (!isSignedIn) {
      // User not signed in, redirect handled by ProtectedRoute
      setLoading(false);
      return;
    }
    
    // User is signed in and Clerk is loaded, fetch dashboard
    if (getToken) {
      fetchDashboard();
    } else {
      console.error("getToken not available even though user is signed in");
      setError("Authentication error - please refresh the page");
      setLoading(false);
    }
  }, [getToken, isLoaded, isSignedIn]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get token and verify it's available
      if (!getToken) {
        console.error("getToken function not available");
        setError("Authentication not available");
        setLoading(false);
        return;
      }
      
      const headers = await getAuthHeaders(getToken);
      
      // Log if token is missing (for debugging)
      if (!headers["Authorization"]) {
        console.error("❌ No Authorization header - token may not be available");
        console.log("getToken function:", getToken);
        console.log("isLoaded:", isLoaded, "isSignedIn:", isSignedIn);
        setError("Authentication token not available. Please sign in again.");
        setLoading(false);
        return;
      }
      
      console.log("📤 Sending request to /api/v1/dashboard with Authorization header");
      
      const res = await fetch("/api/v1/dashboard", {
        headers
      });
      if (!res.ok) {
        const errorText = await res.text();
        console.error("Dashboard API error:", res.status, errorText);
        setError(`Failed to load dashboard: ${res.status === 401 ? "Unauthorized" : "Server error"}`);
        setLoading(false);
        return;
      }
      const json = await res.json();
      setData(json);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      setError("Failed to load dashboard. Please try again.");
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center space-y-2">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-muted-foreground">Loading dashboard...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center space-y-4">
        <p className="text-destructive font-medium">{error}</p>
        <Button onClick={fetchDashboard}>Retry</Button>
      </div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="page-heading text-2xl sm:text-3xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Your financial overview</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button onClick={() => navigate("/income")} size="sm" className="flex-1 sm:flex-none">
            + Income
          </Button>
          <Button variant="outline" onClick={() => navigate("/expenses")} size="sm" className="flex-1 sm:flex-none">
            + Expense
          </Button>
          <Button variant="outline" onClick={() => navigate("/savings")} size="sm" className="flex-1 sm:flex-none">
            + Saving
          </Button>
        </div>
      </div>

      {/* Balance and Savings - 50/50 Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Balance Card - Hero */}
        <Card className="bg-gradient-to-br from-[var(--primary)] via-[var(--secondary)] to-[var(--tertiary)] text-primary-foreground border-0">
          <CardHeader>
            <CardTitle className="text-sm font-medium opacity-90">Available Balance</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="text-3xl sm:text-4xl font-bold tracking-tight">
              {formatCurrency(data.remainingBalance, settings.currencySymbol)}
            </div>
            <p className="text-sm opacity-80 mt-2">Remaining after all expenses</p>
          </CardContent>
        </Card>

        {/* Savings Summary */}
        {data.totalMonthlySavings > 0 ? (
          <Card className="border-0 bg-gradient-to-br from-[var(--success)]/20 to-[var(--success)]/10">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Monthly Savings</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: 'var(--success)' }}>
                {formatCurrency(data.totalMonthlySavings, settings.currencySymbol)}
              </div>
              <p className="text-sm text-muted-foreground">
                {data.savings?.length || 0} {data.savings?.length === 1 ? 'saving' : 'savings'} active
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 bg-gradient-to-br from-[var(--success)]/20 to-[var(--success)]/10 opacity-50">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Monthly Savings</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: 'var(--success)' }}>
                {formatCurrency(0, settings.currencySymbol)}
              </div>
              <p className="text-sm text-muted-foreground">No savings goals yet</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border-0 bg-gradient-to-br from-[var(--tertiary)]/20 to-[var(--tertiary)]/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Monthly Income</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-[var(--tertiary)]">
              {formatCurrency(data.totalIncome, settings.currencySymbol)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-gradient-to-br from-[var(--secondary)]/20 to-[var(--secondary)]/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Monthly Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-[var(--secondary)]">
              {formatCurrency(data.monthlyExpenses, settings.currencySymbol)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-gradient-to-br from-[var(--primary)]/20 to-[var(--primary)]/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Regular Bills</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-[var(--primary)]">
              {formatCurrency(data.regularBillsTotal, settings.currencySymbol)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-gradient-to-br from-[var(--highlight)]/20 to-[var(--highlight)]/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Provisions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-[var(--highlight)]">
              {formatCurrency(data.provisionsTotal, settings.currencySymbol)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Spending Sheet */}
      <div>
        <h2 className="text-xl sm:text-2xl font-semibold mb-4">Monthly Spending</h2>
        <Card>
          <CardHeader>
            <CardTitle>Combined Expenses & Savings</CardTitle>
            <CardDescription>All your monthly outgoings, including provisions for future bills and savings.</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filter Buttons */}
            <div className="flex gap-2 mb-6 pb-4 overflow-x-auto">
              <Button
                variant={filter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("all")}
                className="rounded-full whitespace-nowrap"
              >
                All
              </Button>
              <Button
                variant={filter === "regular" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("regular")}
                className="rounded-full whitespace-nowrap"
              >
                Regular Bills
              </Button>
              <Button
                variant={filter === "provision" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("provision")}
                className="rounded-full whitespace-nowrap"
              >
                Future Provisions
              </Button>
              <Button
                variant={filter === "savings" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("savings")}
                className="rounded-full whitespace-nowrap"
              >
                Savings
              </Button>
            </div>
          {(!data.regularExpenses || data.regularExpenses.length === 0) && (!data.futureExpenses || data.futureExpenses.length === 0) && (!data.savings || data.savings.length === 0) ? (
            <p className="text-sm text-muted-foreground p-4 text-center">No expenses or savings recorded yet.</p>
          ) : (
            <div className="bg-[var(--surface-container-high)] rounded-2xl overflow-hidden">
              {/* Header Row */}
              <div className="flex justify-between items-center pb-2 px-4 py-2 text-sm font-medium text-muted-foreground border-b border-background/50">
                <div className="flex-1">Item</div>
                <div className="flex-1 hidden md:block text-center">Type</div>
                <div className="w-[120px] text-right">Monthly Cost</div>
              </div>

              {/* Merged List with Filtering */}
              {[
                ...(data.regularExpenses || []).map(e => ({ ...e, type: "Regular", monthly: e.monthly || e.amount })),
                ...(data.futureExpenses || []).map(e => ({ ...e, type: "Provision", monthly: e.monthly })),
                ...(data.savings || []).map(s => ({ ...s, type: "Saving", monthly: s.monthly || s.amount, category: s.category }))
              ]
                .filter((item: any) => {
                  if (filter === "all") return true;
                  if (filter === "regular") {
                    return item.type === "Regular" && (item.frequency === "Weekly" || item.frequency === "Monthly");
                  }
                  if (filter === "provision") {
                    // Include Annual recurring expenses AND future one-time expenses
                    return (item.type === "Regular" && (item.frequency === "Annual" || item.frequency === "Yearly")) ||
                      item.type === "Provision";
                  }
                  if (filter === "savings") {
                    return item.type === "Saving";
                  }
                  return true;
                })
                .sort((a, b) => b.monthly - a.monthly)
                .map((item: any, idx) => ( // Using any for joined sorting, ideally strict types
                  <div key={`${item.type}-${item.id}`} className="flex justify-between items-center py-3 px-4 border-b border-background/50 last:border-b-0">
                    <div className="flex-1">
                      <p className="font-medium">{item.category}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.type === "Saving"
                          ? item.goal_amount && item.goal_date
                            ? `Goal: ${formatCurrency(item.goal_amount, settings.currencySymbol)} by ${formatDate(item.goal_date, settings.dateFormat)}`
                            : `Started: ${formatDate(item.date, settings.dateFormat)}`
                          : item.type === "Provision"
                            ? `Saving for ${formatCurrency(item.amount, settings.currencySymbol)} due ${formatDate(item.date, settings.dateFormat)}`
                            : (item.frequency === "Annual" || item.frequency === "Yearly")
                              ? `Annual - Due ${formatDate(item.nextDueDate, settings.dateFormat)}`
                              : `Due ${formatDate(item.nextDueDate, settings.dateFormat)} (${item.frequency || "Monthly"})`
                        }
                      </p>
                    </div>
                    <div className="flex-1 hidden md:block text-center">
                      <span className={`text-xs px-2 py-1 rounded-full ${item.type === "Saving"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                        : item.type === "Provision"
                          ? "bg-orange-100 text-orange-800"
                          : (item.frequency === "Annual" || item.frequency === "Yearly")
                            ? "bg-purple-100 text-purple-800"
                            : "bg-slate-100 text-slate-800"
                        }`}>
                        {item.type === "Saving"
                          ? "Saving"
                          : item.type === "Provision"
                            ? "Provision"
                            : (item.frequency === "Annual" || item.frequency === "Yearly")
                              ? "Annual Provision"
                              : "Regular"}
                      </span>
                    </div>
                    <div className="w-[120px] text-right font-bold" style={{ color: 'var(--destructive)' }}>
                      -{formatCurrency(item.monthly, settings.currencySymbol)}
                    </div>
                  </div>
                ))}

              {/* Totals Row */}
              <div className="flex justify-between items-center px-4 py-3">
                <span className="font-semibold text-sm sm:text-base">
                  {filter === "all" && "Total Monthly Spending"}
                  {filter === "regular" && "Regular Bills Total"}
                  {filter === "provision" && "Provisions Total"}
                  {filter === "savings" && "Savings Total"}
                </span>
                <span className="font-bold text-lg sm:text-xl" style={{ color: 'var(--destructive)' }}>
                  -{formatCurrency(
                    [
                      ...(data.regularExpenses || []).map(e => ({ ...e, type: "Regular", monthly: e.monthly || e.amount })),
                      ...(data.futureExpenses || []).map(e => ({ ...e, type: "Provision", monthly: e.monthly })),
                      ...(data.savings || []).map(s => ({ ...s, type: "Saving", monthly: s.monthly || s.amount }))
                    ]
                      .filter((item: any) => {
                        if (filter === "all") return true;
                        if (filter === "regular") {
                          return item.type === "Regular" && (item.frequency === "Weekly" || item.frequency === "Monthly");
                        }
                        if (filter === "provision") {
                          // Include Annual recurring expenses AND future one-time expenses
                          return (item.type === "Regular" && (item.frequency === "Annual" || item.frequency === "Yearly")) ||
                            item.type === "Provision";
                        }
                        if (filter === "savings") {
                          return item.type === "Saving";
                        }
                        return true;
                      })
                      .reduce((sum, item) => sum + item.monthly, 0),
                    settings.currencySymbol
                  )}
                </span>
              </div>
            </div>
          )}
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
